"use client"
import React, { useRef, useEffect, useMemo, useState } from "react"
import { usePriorityData } from "@/features/priority/hooks/use-priority-data"
import { useRegionId } from "@/shared/hooks/use-region-id"
import { HOMEOWNERS_ROUTES } from "@/shared/constants/pages-routes"
import { buildHomeownersDetailUrlWithReturn } from "@/shared/lib/utils/navigation-helpers"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"
import {
  shouldMaskHomeownerData,
  maskHomeownerLastName,
  maskHomeownerAddress,
} from "@/shared/lib/utils/homeowner-privacy.utils"
import { Button } from "@/shared/components/ui/button"
import { RefreshCw, Download, Search, X } from "lucide-react"
import { useRouter, usePathname, useSearchParams } from "next/navigation"
import { useRegion } from "@/shared/hooks/use-region"
import { buildRegionAwareHref } from "@/shared/lib/utils"
import { Input } from "@/shared/components/ui/input"
import { getDisplayAddressValue, isUnknownLocationValue } from "../lib/unknown-location"
import { OrganizationOptionsProvider } from "../context/organization-options-context"
import PriorityFilterButton from "@/features/priority/components/priority-filter-button"
import PriorityFilterIndicator from "@/features/priority/components/priority-filter-indicator"
import {
  getPriorityFiltersFromSearchParams,
  hasActivePriorityFilters,
  priorityCardMatchesFilters,
  clearPriorityFilterParams,
} from "@/features/priority/lib/priority-filters"

function normalizePrioritySearchValue(value: unknown): string {
  if (value == null) return ""
  if (Array.isArray(value)) return value.map(normalizePrioritySearchValue).join(" ")
  return String(value).toLowerCase()
}

function priorityCardMatchesSearch(priorityItem: any, workScopeData: any, searchQuery: string): boolean {
  if (!searchQuery) return true

  const isObject = typeof priorityItem === "object" && priorityItem !== null
  const searchFields = isObject
    ? [
        priorityItem.name,
        priorityItem.homeId,
        priorityItem.address,
        priorityItem.status,
        priorityItem.statusDetail,
        priorityItem.claimedBy,
        priorityItem.isGoodwill ? "Goodwill" : "",
        priorityItem.isReady === false ? "Not Ready" : "Ready",
      ]
    : [`Home #${priorityItem}`, priorityItem]

  searchFields.push(workScopeData.workScope, workScopeData.workScopeDisplay)

  return searchFields.some((field) => normalizePrioritySearchValue(field).includes(searchQuery))
}

function PlanningPriorityTab() {
  const { regionId, isLoading: isLoadingRegion } = useRegionId()
  const { data, isLoading, refresh, error: priorityError } = usePriorityData(regionId || undefined)
  const { session } = useSession()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { regionSlug } = useRegion()
  const [prioritySearchQuery, setPrioritySearchQuery] = useState("")
  const normalizedPrioritySearchQuery = prioritySearchQuery.trim().toLowerCase()
  const matrixFilters = useMemo(
    () => getPriorityFiltersFromSearchParams(searchParams),
    [searchParams]
  )
  const hasMatrixFilters = hasActivePriorityFilters(matrixFilters)
  
  // Refs for scroll containers
  const horizontalScrollRef = useRef<HTMLDivElement>(null)
  const columnScrollRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  // Check if user has permission to refresh (Admin or Base Leader)
  const canRefresh = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
  // Check if user is admin (for CSV export)
  const isAdmin = session?.role === UserRole.ADMIN

  const displayedPriorityData = useMemo(() => {
    const sortedData = [...(data || [])].sort((a, b) => a.workScopeId - b.workScopeId)
    const hasSearch = Boolean(normalizedPrioritySearchQuery)
    if (!hasSearch && !hasMatrixFilters) {
      return sortedData
    }

    const matches = (
      item: { createdAt?: string | null; claimedBy?: string | null; statusDetail?: string | null },
      extras: Record<string, unknown>,
      workScopeData: (typeof sortedData)[number]
    ) => {
      if (!priorityCardMatchesFilters(item, matrixFilters)) return false
      if (!hasSearch) return true
      return priorityCardMatchesSearch(
        { ...item, ...extras },
        workScopeData,
        normalizedPrioritySearchQuery
      )
    }

    return sortedData.map((workScopeData) => ({
      ...workScopeData,
      priorities: {
        ...workScopeData.priorities,
        ready: (workScopeData?.priorities?.ready || []).filter((item) =>
          matches(item, { isReady: true, isGoodwill: false }, workScopeData)
        ),
        notReady: (workScopeData?.priorities?.notReady || []).filter((item) =>
          matches(item, { isReady: false, isGoodwill: false }, workScopeData)
        ),
        goodwill: (workScopeData?.priorities?.goodwill || []).filter((item) =>
          matches(
            item,
            { isReady: item.status === "Ready", isGoodwill: true },
            workScopeData
          )
        ),
      },
    }))
  }, [data, normalizedPrioritySearchQuery, hasMatrixFilters, matrixFilters])

  // Exclude homes that only appear in goodwill columns (no ready/not-ready scopes)
  const displayedUniqueHomes = useMemo(() => (
    new Set<number>(
      displayedPriorityData.flatMap(ws => [
        ...(ws?.priorities?.ready || []).map(p => p.homeId),
        ...(ws?.priorities?.notReady || []).map(p => p.homeId),
      ])
    ).size
  ), [displayedPriorityData])
  
  // Save scroll positions before navigation
  const saveScrollPositions = () => {
    if (typeof window === 'undefined') return
    
    // Save horizontal scroll
    if (horizontalScrollRef.current) {
      const horizontalScroll = horizontalScrollRef.current.scrollLeft
      sessionStorage.setItem('priority_matrix_horizontal_scroll', horizontalScroll.toString())
    }
    
    // Save vertical scroll for each column
    const columnScrolls: Record<string, number> = {}
    columnScrollRefs.current.forEach((element, workScope) => {
      if (element) {
        columnScrolls[workScope] = element.scrollTop
      }
    })
    sessionStorage.setItem('priority_matrix_column_scrolls', JSON.stringify(columnScrolls))
  }
  
  // Restore scroll positions on mount
  useEffect(() => {
    if (typeof window === 'undefined' || isLoading || !data) return
    
    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      // Restore horizontal scroll
      const savedHorizontal = sessionStorage.getItem('priority_matrix_horizontal_scroll')
      if (savedHorizontal && horizontalScrollRef.current) {
        horizontalScrollRef.current.scrollLeft = parseInt(savedHorizontal, 10)
      }
      
      // Restore vertical scroll for each column
      const savedColumnScrolls = sessionStorage.getItem('priority_matrix_column_scrolls')
      if (savedColumnScrolls) {
        try {
          const columnScrolls: Record<string, number> = JSON.parse(savedColumnScrolls)
          columnScrollRefs.current.forEach((element, workScope) => {
            if (element && columnScrolls[workScope] !== undefined) {
              element.scrollTop = columnScrolls[workScope]
            }
          })
        } catch (e) {
          console.error('Error restoring column scroll positions:', e)
        }
      }
    }, 100)
    
    return () => clearTimeout(timer)
  }, [isLoading, data])

  const exportToCSV = () => {
    if (!data || data.length === 0) return

    // Blacklist of non-rebuild work scopes to exclude
    const blacklistedScopes = [
      'heavy-equipment-demo',
      'tree-work',
      'exterior-debris-removal',
      'tarp-roof',
      'm-and-g-crawl-space',
      'muck-and-gut-interior',
      'mold-remediation',
      'insulate-crawl-space',
      'misc'
    ]
    
    // Filter out blacklisted scopes (data already comes from priorityTable = true)
    const allWorkScopes = displayedPriorityData.filter(ws => !blacklistedScopes.includes(ws.workScope))

    // Collect all unique home IDs and their details from all work scopes
    const allHomes = new Map<number, { name: string; address: string }>()
    
    allWorkScopes.forEach(workScope => {
      const allItems = [
        ...(workScope.priorities.ready || []),
        ...(workScope.priorities.notReady || []),
        ...(workScope.priorities.goodwill || [])
      ]
      allItems.forEach(item => {
        if (!allHomes.has(item.homeId)) {
          allHomes.set(item.homeId, {
            name: item.name,
            address: (item as any).address || ""
          })
        }
      })
    })

    // Create a map: homeId -> workScope -> statusDetail
    const homeWorkScopeStatus = new Map<number, Map<string, string>>()
    
    allWorkScopes.forEach(workScope => {
      const allItems = [
        ...(workScope.priorities.ready || []),
        ...(workScope.priorities.notReady || []),
        ...(workScope.priorities.goodwill || [])
      ]
      allItems.forEach(item => {
        if (!homeWorkScopeStatus.has(item.homeId)) {
          homeWorkScopeStatus.set(item.homeId, new Map())
        }
        const statusDetail = (item as any).statusDetail
        const displayStatus = (statusDetail == null || statusDetail === 'Unknown') ? 'Missing Status' : (statusDetail || '')
        homeWorkScopeStatus.get(item.homeId)!.set(workScope.workScope, displayStatus)
      })
    })

    // Create CSV header
    const headers = ['Home ID', 'Homeowner Name', 'Address', ...allWorkScopes.map(ws => ws.workScope)]
    const csvRows = [headers.join(',')]

    // Create a row for each home
    const sortedHomeIds = Array.from(allHomes.keys()).sort((a, b) => a - b)
    
    sortedHomeIds.forEach(homeId => {
      const home = allHomes.get(homeId)!
      const row = [
        homeId.toString(),
        `"${home.name.replace(/"/g, '""')}"`,
        `"${getDisplayAddressValue(home.address).replace(/"/g, '""')}"`,
        ...allWorkScopes.map(workScope => {
          const status = homeWorkScopeStatus.get(homeId)?.get(workScope.workScope) || ''
          return status ? `"${status.replace(/"/g, '""')}"` : ''
        })
      ]
      csvRows.push(row.join(','))
    })

    // Create blob and download
    const csvContent = csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    
    link.setAttribute('href', url)
    link.setAttribute('download', `rebuild_scopes_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (isLoadingRegion || isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2">Loading priorities...</span>
      </div>
    )
  }

  if (!regionId) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">
          {isLoadingRegion ? 'Loading region...' : `No region selected (slug: ${regionSlug || 'none'})`}
        </p>
        {priorityError && (
          <p className="text-red-500 mt-2">Error: {priorityError}</p>
        )}
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">No priority data available</p>
        {priorityError && (
          <p className="text-red-500 mt-2">Error: {priorityError}</p>
        )}
        {canRefresh && (
          <Button 
            onClick={refresh} 
            variant="default"
            className="bg-primary hover:bg-primary/90 text-white font-bold mt-4"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        )}
      </div>
    )
  }

  const clearAllPriorityFilters = () => {
    setPrioritySearchQuery("")
    if (hasMatrixFilters) {
      const next = clearPriorityFilterParams(searchParams)
      const query = next.toString()
      router.push(query ? `${pathname}?${query}` : pathname)
    }
  }

  return (
    <OrganizationOptionsProvider>
    <div className="flex flex-col p-1 -m-1 h-[calc(100vh-250px)] max-h-[calc(100vh-250px)] md:h-[calc(100vh-179px)] md:max-h-[calc(100vh-179px)]">
      <div className="flex flex-col gap-2 flex-shrink-0 px-1 mb-1">
        <div className="flex flex-col gap-2 md:flex-row md:justify-between md:items-center">
        <h2 className="text-lg font-semibold">{displayedUniqueHomes} Unique Homes</h2>
        <div className="flex flex-wrap items-center gap-2">
          <PriorityFilterButton />
          <div className="relative min-w-[230px] flex-1 md:w-[320px] md:flex-none">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              value={prioritySearchQuery}
              onChange={(event) => setPrioritySearchQuery((event.target as HTMLInputElement).value)}
              placeholder="Search in the Priority Matrix..."
              className="h-8 pl-8 pr-8 text-sm"
            />
            {prioritySearchQuery.trim().length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setPrioritySearchQuery("")}
                className="absolute right-0.5 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear priority matrix search"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
          {(prioritySearchQuery.trim().length > 0 || hasMatrixFilters) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearAllPriorityFilters}
              className="h-8 shrink-0"
            >
              Clear All
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={exportToCSV}
              className="hidden md:flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export Rebuild Scopes
            </Button>
          )}
          {canRefresh && (
            <Button
              variant="default"
              size="sm"
              onClick={refresh}
              className="bg-primary hover:bg-primary/90 text-white font-bold flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          )}
        </div>
        </div>
        <PriorityFilterIndicator />
      </div>

      <div 
        ref={horizontalScrollRef}
        className="flex-1 overflow-x-auto overflow-y-hidden min-h-0"
      >
        <div className="flex gap-1 h-full" style={{ minWidth: 'max-content' }}>
          {displayedPriorityData.map((workScopeData) => {
            
            // Combine ready, not-ready, then goodwill at bottom (goodwill in same priority order)
            const readyHomes = (workScopeData?.priorities?.ready || []).map(item => ({ ...item, isReady: true, isGoodwill: false }))
            const notReadyHomes = (workScopeData?.priorities?.notReady || []).map(item => ({ ...item, isReady: false, isGoodwill: false }))
            const goodwillHomes = (workScopeData?.priorities?.goodwill || []).map(item => ({
              ...item,
              isReady: item.status === "Ready",
              isGoodwill: true
            }))
            const priorities = [...readyHomes, ...notReadyHomes, ...goodwillHomes]
            
            // Miscellaneous contains only goodwill items, so use its goodwill totals.
            const isGoodwillOnlyScope = workScopeData.workScope === 'misc'
            const totalNeeded = isGoodwillOnlyScope
              ? workScopeData.goodwillTotalNeeded || 0
              : workScopeData.totalNeeded || 0
            const completed = isGoodwillOnlyScope
              ? workScopeData.goodwillCompleted || 0
              : workScopeData.completed || 0
            const percentage = isGoodwillOnlyScope
              ? workScopeData.goodwillPercentage || 0
              : workScopeData.percentage || 0
            
            return (
              <div key={workScopeData.workScope} className="border rounded-lg p-1 flex-shrink-0 flex flex-col h-full" style={{ width: '180px' }}>
                <h3 className="font-semibold text-xs mb-0.5 flex-shrink-0 text-center">{workScopeData.workScope === 'misc' ? 'Misc Goodwill' : workScopeData.workScopeDisplay} ({priorities.length})</h3>
                <div className="text-xs text-center mb-1 pb-1 border-b flex-shrink-0 text-muted-foreground">
                  {completed} / {totalNeeded} ({percentage}%)
                </div>
                <div 
                  ref={(el) => {
                    if (el) {
                      columnScrollRefs.current.set(workScopeData.workScope, el)
                    } else {
                      columnScrollRefs.current.delete(workScopeData.workScope)
                    }
                  }}
                  className="space-y-0 flex-1 overflow-y-auto"
                >
                  {priorities.length > 0 ? (
                    priorities.map((priorityItem, index) => {
                      // Handle both old format (numbers) and new format (objects)
                      const isObject = typeof priorityItem === 'object' && priorityItem !== null
                      const rawName = isObject ? priorityItem.name : `Home #${priorityItem}`
                      const homeId = isObject ? priorityItem.homeId : priorityItem
                      const homeownerId = isObject ? (priorityItem as any).homeownerId : undefined
                      const homeownerUserId = isObject ? (priorityItem as any).homeownerUserId : undefined
                      const address = isObject ? (priorityItem as any).address : undefined
                      const statusDetail = isObject ? (priorityItem as any).statusDetail : undefined
                      const rawClaimedBy = isObject ? (priorityItem as any).claimedBy : undefined
                      // Normalize: strip brackets/quotes — show "New Day" not ["New Day"]; handle array or JSON string
                      const claimedByDisplay = (() => {
                        if (rawClaimedBy == null || rawClaimedBy === "No Need") return null
                        if (Array.isArray(rawClaimedBy)) {
                          const joined = (rawClaimedBy as string[]).filter((v) => v && v !== "No Need").join(", ")
                          return joined || null
                        }
                        if (typeof rawClaimedBy === "string") {
                          const s = rawClaimedBy.trim()
                          if (!s) return null
                          try {
                            const parsed = JSON.parse(s)
                            if (Array.isArray(parsed)) {
                              const joined = parsed.filter((v: unknown) => typeof v === "string" && v !== "No Need").join(", ")
                              return joined || null
                            }
                          } catch {
                            // not JSON, use as-is
                          }
                          return s
                        }
                        return null
                      })()
                      const isReady = isObject ? priorityItem.isReady !== false : true // Default to ready for backward compatibility
                      const isGoodwill = isObject && (priorityItem as { isGoodwill?: boolean }).isGoodwill === true
                      const mask = shouldMaskHomeownerData(session?.role, session?.sub, homeownerUserId)
                      const name = mask ? maskHomeownerLastName(rawName) : rawName
                      const displayAddress = address
                        ? (mask && !isUnknownLocationValue(address)
                            ? maskHomeownerAddress(address)
                            : getDisplayAddressValue(address))
                        : undefined
                      
                      // Dynamic styling: goodwill = light transparent green (distinct from grey Not Ready)
                      const goodwillBg = isGoodwill ? "bg-green-500/15 hover:bg-green-500/25" : ""
                      const itemBase = isGoodwill
                        ? `text-xs py-1 px-1 rounded cursor-pointer border min-h-[3rem] flex items-start shadow-sm ${goodwillBg}`
                        : isReady
                          ? "text-xs py-1 px-1 bg-white rounded cursor-pointer hover:bg-gray-50 border min-h-[3rem] flex items-start shadow-sm"
                          : "text-xs py-1 px-1 bg-gray-300 rounded cursor-pointer hover:bg-gray-400 border min-h-[3rem] flex items-start opacity-70"
                      const itemClasses = `${itemBase} ${(statusDetail == null || statusDetail === 'Unknown') ? 'border-red-400 bg-yellow-200 hover:bg-yellow-200' : ''}`
                      
                      const textClasses = isReady
                        ? "font-medium text-gray-900 break-words"
                        : "font-medium text-gray-600 break-words"
                      
                      const handleClick = () => {
                        if (homeownerId) {
                          // Save scroll positions before navigating
                          saveScrollPositions()
                          router.push(buildRegionAwareHref(regionSlug, buildHomeownersDetailUrlWithReturn(homeownerId)))
                        }
                      }
                      
                      return (
                        <div 
                          key={`${homeId}-${index}`} 
                          className={itemClasses}
                          onClick={handleClick}
                          style={{ cursor: homeownerId ? 'pointer' : 'default' }}
                        >
                          <div className={textClasses} title={`${name}${isGoodwill ? ' (Goodwill)' : ''} ${isReady ? '(Ready)' : '(Not Ready)'}`}>
                            <div>
                              {name}
                              {isGoodwill && workScopeData.workScope !== 'misc' && <span className="text-[10px] text-green-700 font-medium ml-1">(Goodwill)</span>}
                              {!isReady && <span className="text-xs text-gray-400 ml-1">⏳</span>}
                              {(statusDetail == null || statusDetail === 'Unknown') && (
                                <span className="ml-1 text-[10px] text-red-600">update status</span>
                              )}
                            </div>
                            {displayAddress ? (
                              <div className="text-[10px] text-gray-500 leading-tight">{displayAddress}</div>
                            ) : null}
                            {claimedByDisplay ? (
                              <div className="text-[10px] text-gray-600 leading-tight mt-0.5 font-semibold italic">{claimedByDisplay}</div>
                            ) : null}
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div className="text-xs text-muted-foreground">No priorities</div>
                  )}
                </div>
                <div className="mt-1 pt-1 border-t text-xs text-muted-foreground flex-shrink-0">
                  <div className="flex justify-between items-center gap-x-1 flex-nowrap">
                    <span>Ready: {readyHomes.length}</span>
                    <span>Not Ready: {notReadyHomes.length}</span>
                    {goodwillHomes.length > 0 && <span>Goodwill: {goodwillHomes.length}</span>}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
    </OrganizationOptionsProvider>
  )
}

export default PlanningPriorityTab
